import ConnectedPageActions from '../actions/ConnectedPageCRUDActions';
import SubscriptionActions from '../actions/SubscriptionCRUDActions';
import Actions from '../actions/actions';
import { setBusy, setUnBusy, update } from './common/commonReducers';
import { ConnectedPage } from '../interfaces/connected-page';

type SetSubscriptionAction = {
  status: string;
  type: string;
};

const initialState: ConnectedPage = {
  entities: [],
  availablePages: { data: [], next: null, prev: null },
  basicPagesList: [],
  busy: false,
  hasPermission: true,
  pagination: null,
  pageDetails: undefined,
};

const setAvailablePages = (state: ConnectedPage, action): ConnectedPage => {
  const {
    availablePages: { data, next, prev },
  } = action;
  const {
    availablePages: { data: currentData, prev: currentPrev },
  } = state;

  const nextData =
    prev && prev !== currentPrev ? currentData.concat(data) : data;
  const nextPages = { data: nextData, prev, next };

  return {
    ...state,
    busy: false,
    availablePages: nextPages,
  };
};

const setBasicPagesList = (state: ConnectedPage, action): ConnectedPage => {
  const { basicPagesList } = action;

  return {
    ...state,
    busy: false,
    basicPagesList,
  };
};

const setAccessiblePagesByRoles = (
  state: ConnectedPage,
  action,
): ConnectedPage => {
  const { accessiblePagesByRoles } = action;

  return {
    ...state,
    busy: false,
    accessiblePagesByRoles,
  };
};

const retrievedAll = (state: ConnectedPage, action): ConnectedPage => {
  const { facebookPages, pagination } = action.entities;
  return {
    ...state,
    busy: false,
    entities: facebookPages,
    pagination,
  };
};

const setSubscriptionMessagingStatus = (
  state: ConnectedPage,
  action: SetSubscriptionAction,
) => {
  const { status } = action;
  const { pageDetails } = state;
  const nextPageDetails = {
    ...pageDetails,
    subscriptionMessagingStatus: status,
  };

  return {
    ...state,
    pageDetails: nextPageDetails,
    busy: false,
  };
};

export default function connectedPage(state = initialState, action) {
  switch (action.type) {
    case ConnectedPageActions.unbusyAction: {
      return {
        ...state,
        busy: false,
      };
    }
    case ConnectedPageActions.setPermission: {
      const { hasPermission } = action;
      return {
        ...state,
        hasPermission,
      };
    }
    case ConnectedPageActions.fetchConnectedPageAction: {
      const { page } = action;
      return {
        ...state,
        busy: false,
        pageDetails: page,
      };
    }
    case ConnectedPageActions.nullifyConnectedPageAction: {
      return {
        ...state,
        busy: false,
        pageDetails: null,
      };
    }
    case SubscriptionActions.swappedPagesAction: {
      const {
        facebookPageId,
        subscription,
        newPageId,
        newPageStatistics,
        oldPageStatistics,
      } = action.data;
      const prepareEntities = entities => {
        const oldPageIndex = entities.findIndex(p => p.id === facebookPageId);
        const newPageIndex = entities.findIndex(p => p.id === newPageId);
        if (newPageIndex >= 0 && oldPageIndex >= 0) {
          return {
            [oldPageIndex]: {
              ...entities[oldPageIndex],
              subscription: null,
              subscriptions: [],
              overagesEnabled: false,
              currentOutgoingMessagesStatistic:
                oldPageStatistics.currentOutgoingMessagesStatistic,
              outgoingMessagesLimit: oldPageStatistics.outgoingMessagesLimit,
            },
            [newPageIndex]: {
              ...entities[newPageIndex],
              subscription,
              subscriptions: [subscription],
              overagesEnabled: entities[oldPageIndex].overagesEnabled,
              currentOutgoingMessagesStatistic:
                newPageStatistics.currentOutgoingMessagesStatistic,
              outgoingMessagesLimit: newPageStatistics.outgoingMessagesLimit,
            },
          };
        }
        return {};
      };
      return {
        ...state,
        basicPagesList: Object.assign(
          [],
          state.basicPagesList.slice(),
          prepareEntities(state.basicPagesList),
        ),
        entities: Object.assign(
          [],
          state.entities.slice(),
          prepareEntities(state.entities),
        ),
      };
    }
    case SubscriptionActions.setOveragesEnabledAction: {
      const { facebookPageId, overagesEnabled } = action.data;
      if (facebookPageId) {
        const entities = state.basicPagesList;
        const pageIndex = entities.findIndex(p => p.id === facebookPageId);
        const newEntity = {
          [pageIndex]: { ...entities[pageIndex], overagesEnabled },
        };
        const newEntities = Object.assign([], entities.slice(), newEntity);
        return {
          ...state,
          basicPagesList: newEntities,
        };
      }
      return state;
    }
    case ConnectedPageActions.adPublished:
      const { key } = action;
      return { ...state, pageDetails: { ...state.pageDetails, [key]: true } };
    case ConnectedPageActions.busyAction:
      return setBusy(state);
    case ConnectedPageActions.retrievedAllAction:
      return retrievedAll(state, action);
    case ConnectedPageActions.updateAction:
      return update(state, action);
    case ConnectedPageActions.retrievedAvailablePagesAction:
      return setAvailablePages(state, action);
    case ConnectedPageActions.retrievedBasicListAction:
      return setBasicPagesList(state, action);
    case ConnectedPageActions.fetchAccessiblePagesByRoleAction:
      return setAccessiblePagesByRoles(state, action);
    case ConnectedPageActions.resetAvailablePagesAction: {
      const { availablePages } = initialState;
      return {
        ...state,
        availablePages,
      };
    }
    case ConnectedPageActions.retrievedSubscriptionMessagingStatusAction:
      return setSubscriptionMessagingStatus(state, action);
    case Actions.RESET:
      return initialState;
    case Actions.UNBUSY:
      return setUnBusy(state);
    default:
      return state;
  }
}
